/**
 * FloatingLabelInput
 *
 * Form input with floating label animation used in auth/onboarding modals.
 * Label moves up and scales down when input is focused or has value.
 */
"use client";

import { useState, type ReactNode } from "react";

import { colors, modalSpacing } from "@/styles/design-tokens";

interface FloatingLabelInputProps {
  type?: "text" | "email" | "password";
  value: string;
  onChange: (value: string) => void;
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Optional element to render on the right side (e.g., password visibility toggle) */
  rightSlot?: ReactNode;
  className?: string;
}

export function FloatingLabelInput({
  type = "text",
  value,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  rightSlot,
  className = "",
}: FloatingLabelInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={`relative ${className}`} style={{ height: modalSpacing.inputFieldHeight }}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        className="text-input-modal text-ink absolute left-0 top-0 w-full bg-transparent px-0 pt-3 outline-none"
        style={{
          height: modalSpacing.inputFieldHeight,
          border: "none",
          borderBottom: `1px solid ${colors.divider.divider1}`,
          borderRadius: "0",
          paddingRight: rightSlot ? "2.5rem" : "0",
        }}
        aria-label={ariaLabel || label}
      />
      <label
        className="text-input-modal pointer-events-none absolute left-0 top-3 transition-all duration-200"
        style={{
          color: colors.text.floatingLabelInactive,
          transform:
            isFocused || value ? "translateY(-12px) scale(0.85)" : "translateY(0) scale(1)",
          transformOrigin: "left top",
        }}
      >
        {label}
      </label>
      {/* Right slot (e.g., password toggle) */}
      {rightSlot && <div className="absolute right-0 top-1/2 -translate-y-1/2">{rightSlot}</div>}
      {/* Active underline */}
      <div
        className="absolute bottom-0 left-0 h-[0.5px] bg-black transition-all duration-200"
        style={{ width: isFocused ? "100%" : "0" }}
      />
    </div>
  );
}
